import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Grid
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'
import { useSelector } from 'react-redux'

import { fetchAllSubject } from 'api'
import CloudFileUploader from 'app/components/CloudFileUploader'
import { AutoComplete } from 'app/components/generic/FormFields'
import {
  dispatchUpdateAnnouncementFormField,
  dispatchUpdateAnnouncementFieldError
} from 'app/store/dispatcher'
import {
  selectAnnouncementFormData,
  selectAnnouncementErrors
} from 'app/store/selectors'

import { AnnouncementFormFields, validateRequired } from './config'
import * as Styles from './styles'

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),
    width: '100%'
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end'
  }
}))

const AnnouncementForm = ({ isEditMode = false }) => {
  const formData = useSelector(selectAnnouncementFormData)
  console.log('formData', formData)
  const fieldErrors = useSelector(selectAnnouncementErrors)
  const classes = useStyles()

  const modifiedFormFields = React.useMemo(() => {
    // Add subject field if type is Trial or Playdate and batch_type is set
    const subjectField =
      (formData?.type === 'Trial' || formData?.type === 'Playdate') &&
      formData?.batch_type !== ''
        ? {
            name: 'subject',
            label: 'Subject',
            inputType: 'autocomplete',
            required: true,
            validator: (value) => validateRequired(value, 'Subject'),
            disabled: isEditMode, // Disable subject field in edit mode
            key: `subject-${formData.batch_type}`,
            fetchValues: async (searchedText) => {
              try {
                const queries = {
                  limit: 600,
                  offset: 0,
                  batch_type: formData?.batch_type,
                  ...(formData?.type === 'Playdate'
                    ? { subject_category_name: 'Playdate' }
                    : {})
                }

                if (searchedText) {
                  queries.name = searchedText
                }

                const response = await fetchAllSubject({ queries })

                return response.results.map((item) => ({
                  uuid: item?.uuid,
                  value: item.uuid,
                  name: item.name || '',
                  description: item.description,
                  centres: item.centres,
                  app_image: item.app_image
                }))
              } catch (error) {
                console.log('Error in fetching subjects :- ', error)
                throw error
              }
            }
          }
        : null

    return [
      ...AnnouncementFormFields(isEditMode).slice(0, 3), // type, subtype
      ...(subjectField ? [subjectField] : []),
      ...AnnouncementFormFields(isEditMode).slice(4) // Include all remaining fields including tags
    ]
  }, [formData.type, formData.batch_type, isEditMode])

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target
    const fieldValue = type === 'checkbox' ? checked : value

    dispatchUpdateAnnouncementFormField(name, fieldValue)

    // Auto-fill batch_type when announcement type changes
    if (name === 'type') {
      dispatchUpdateAnnouncementFormField('batch_type', '')
      dispatchUpdateAnnouncementFormField('subject', null)
      dispatchUpdateAnnouncementFormField('subtype', '')
      dispatchUpdateAnnouncementFormField('title', '')
      dispatchUpdateAnnouncementFormField('description', '')
      dispatchUpdateAnnouncementFormField('centres', [])
      dispatchUpdateAnnouncementFormField('image', null)
      dispatchUpdateAnnouncementFormField('start_date', '')
      dispatchUpdateAnnouncementFormField('end_date', '')
      dispatchUpdateAnnouncementFormField('is_cta_present', false)
      dispatchUpdateAnnouncementFormField('cta', '')
      dispatchUpdateAnnouncementFormField('cta_link', '')
      dispatchUpdateAnnouncementFormField('event_start_date', '')
      dispatchUpdateAnnouncementFormField('event_end_date', '')
      dispatchUpdateAnnouncementFormField('event_start_time', '')
      dispatchUpdateAnnouncementFormField('event_end_time', '')
      dispatchUpdateAnnouncementFormField('tag_1', '')
      dispatchUpdateAnnouncementFormField('tag_2', '')

      if (fieldValue === 'Trial') {
        dispatchUpdateAnnouncementFormField('batch_type', 'explore')
      } else if (fieldValue === 'Playdate') {
        dispatchUpdateAnnouncementFormField('batch_type', 'event')
      } else if (fieldValue === 'General') {
        dispatchUpdateAnnouncementFormField('batch_type', '')
      }
    }

    // Clear error when user starts typing
    if (fieldErrors[name]) {
      dispatchUpdateAnnouncementFieldError(name, null)
    }
  }

  const handleAutoCompleteChange = (name, value) => {
    dispatchUpdateAnnouncementFormField(name, value)

    // Auto-fill functionality when subject is selected (only in create mode)
    if (name === 'subject' && value && !isEditMode) {
      if (value?.name) {
        dispatchUpdateAnnouncementFormField('title', value.name)
      }
      if (value?.description) {
        dispatchUpdateAnnouncementFormField('description', value.description)
      }
      if (value?.centres && Array.isArray(value?.centres)) {
        dispatchUpdateAnnouncementFormField(
          'centres',
          value?.centres?.map((centre) => ({ value: centre?.uuid, ...centre }))
        )
      }
      if (value?.app_image) {
        dispatchUpdateAnnouncementFormField('image', value.app_image)
      }

      // Auto-fill CTA for Playdate announcements
      if (formData?.type === 'Playdate' && value?.uuid) {
        dispatchUpdateAnnouncementFormField('is_cta_present', true)
        dispatchUpdateAnnouncementFormField('cta', 'Book Now')
        dispatchUpdateAnnouncementFormField(
          'cta_link',
          `openhouse://category/ohsn/program?programType=PLAYDATES&subjectId=${value.uuid}`
        )
      }
    }

    // Clear error when user selects
    if (fieldErrors[name]) {
      dispatchUpdateAnnouncementFieldError(name, null)
    }
  }

  const handleFileChange = (name, file) => {
    dispatchUpdateAnnouncementFormField(name, file)

    if (fieldErrors[name]) {
      dispatchUpdateAnnouncementFieldError(name, null)
    }
  }

  const renderField = (field) => {
    // Check conditional rendering
    if (field?.conditionalRender && !field.conditionalRender(formData)) {
      return null
    }

    // Handle default values
    let fieldValue = formData[field.name]
    if (field?.defaultValue && typeof field?.defaultValue === 'function') {
      fieldValue = field.defaultValue(formData)
    }

    switch (field.inputType) {
      case 'sectionHeading':
        return (
          <Styles.SectionHeading key={field.name}>
            {field.label}
          </Styles.SectionHeading>
        )

      case 'divider':
        return <Styles.Divider key={field.name} />

      case 'notes':
        return (
          <Styles.NotesContainer key={field.name}>
            <Styles.NotesTitle>Note:</Styles.NotesTitle>
            <Styles.NotesList>
              {field.notes.map((note, index) => (
                <Styles.NotesItem key={index}>{note}</Styles.NotesItem>
              ))}
            </Styles.NotesList>
          </Styles.NotesContainer>
        )

      case 'externalLink':
        return (
          <Styles.ExternalLinkContainer key={field.name}>
            <Styles.ExternalLinkText
              onClick={() => window.open(field.linkUrl, '_blank')}
            >
              {field.linkText}
            </Styles.ExternalLinkText>
          </Styles.ExternalLinkContainer>
        )

      case 'select':
        return (
          <FormControl
            key={field.name}
            variant="outlined"
            className={classes.formControl}
            required={field?.required}
            error={!!fieldErrors[field.name]}
          >
            <InputLabel>{field.label}</InputLabel>
            <Select
              name={field.name}
              value={fieldValue}
              onChange={handleInputChange}
              label={field.label}
              error={!!fieldErrors[field.name]}
              disabled={field.disabled}
            >
              {Object.entries(field.valueLabelMappings || {}).map(
                ([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                )
              )}
            </Select>
            {fieldErrors[field.name] && (
              <Box
                component="div"
                style={{
                  color: '#f44336',
                  fontSize: '0.75rem',
                  marginTop: '3px'
                }}
              >
                {fieldErrors[field.name]}
              </Box>
            )}
          </FormControl>
        )

      case 'checkbox':
        return (
          <FormControl
            key={field.name}
            error={!!fieldErrors[field.name]}
            className={classes.formControl}
          >
            <FormControlLabel
              required={field?.required}
              control={
                <Checkbox
                  required={field?.required}
                  name={field.name}
                  checked={fieldValue}
                  onChange={handleInputChange}
                  color="primary"
                />
              }
              label={field.label}
            />
            {fieldErrors[field.name] && (
              <Box
                component="div"
                style={{
                  color: '#f44336',
                  fontSize: '0.75rem',
                  marginTop: '3px'
                }}
              >
                {fieldErrors[field.name]}
              </Box>
            )}
          </FormControl>
        )

      case 'autocomplete':
        return (
          <FormControl
            key={
              field?.key ??
              `${field.name}-${formData?.type}-${JSON.stringify(
                formData?.[field.name] || []
              )}`
            }
            variant="outlined"
            required={field?.required}
            className={classes.formControl}
            error={!!fieldErrors[field.name]}
          >
            <AutoComplete
              async={field.fetchValues ? true : false}
              name={field.name}
              label={field.label}
              multiple={field.multiple}
              disabled={field.disabled}
              fetchValues={
                field.fetchValues
                  ? (searchedText) => field.fetchValues(searchedText, formData)
                  : undefined
              }
              onChange={handleAutoCompleteChange}
              required={field.required}
              defaultValue={fieldValue}
              error={!!fieldErrors[field.name]}
            />
            {fieldErrors[field.name] && (
              <Box
                component="div"
                style={{
                  color: '#f44336',
                  fontSize: '0.75rem',
                  marginTop: '3px'
                }}
              >
                {fieldErrors[field.name]}
              </Box>
            )}
          </FormControl>
        )

      case 'imageUploader':
        return (
          <FormControl
            required={field?.required}
            key={field.name}
            variant="outlined"
            className={classes.formControl}
            error={!!fieldErrors[field.name]}
          >
            <CloudFileUploader
              name={field.name}
              label={field.label}
              onUpload={handleFileChange}
              uploadedFileUrl={fieldValue}
              uploadPreset={field.uploadPreset}
              fileUrlOnly={field.fileUrlOnly}
              setFieldValue={(name, value) =>
                dispatchUpdateAnnouncementFormField(name, value)
              }
            />
            {fieldErrors[field.name] && (
              <Box
                component="div"
                style={{
                  color: '#f44336',
                  fontSize: '0.75rem',
                  marginTop: '3px'
                }}
              >
                {fieldErrors[field.name]}
              </Box>
            )}
          </FormControl>
        )

      case 'date':
        return (
          <TextField
            required={field?.required}
            key={field.name}
            name={field.name}
            label={field.label}
            type="date"
            value={fieldValue}
            onChange={handleInputChange}
            variant="outlined"
            className={classes.formControl}
            InputLabelProps={{ shrink: true }}
            inputProps={field.inputProps}
            error={!!fieldErrors[field.name]}
            helperText={fieldErrors[field.name]}
          />
        )

      case 'time':
        return (
          <TextField
            required={field?.required}
            key={field.name}
            name={field.name}
            label={field.label}
            type="time"
            value={fieldValue}
            onChange={handleInputChange}
            variant="outlined"
            className={classes.formControl}
            InputLabelProps={{ shrink: true }}
            error={!!fieldErrors[field.name]}
            helperText={fieldErrors[field.name]}
          />
        )

      default:
        // text
        return (
          <TextField
            required={field?.required}
            key={field.name}
            name={field.name}
            label={field.label}
            value={fieldValue}
            onChange={handleInputChange}
            variant="outlined"
            className={classes.formControl}
            inputProps={{ maxLength: field.maxLength }}
            error={!!fieldErrors[field.name]}
            helperText={fieldErrors[field.name]}
            disabled={field.disabled}
          />
        )
    }
  }

  const renderFormFields = () => {
    const renderedFields = []
    let i = 0

    while (i < modifiedFormFields.length) {
      const field = modifiedFormFields[i]
      const renderedField = renderField(field)

      if (!renderedField) {
        i++
        continue
      }

      if (field?.halfWidth) {
        const nextField = modifiedFormFields[i + 1]
        const nextRenderedField = nextField ? renderField(nextField) : null

        if (nextField?.halfWidth && nextRenderedField) {
          renderedFields.push(
            <Grid container item spacing={2} key={`row-${i}`}>
              <Grid item xs={6}>
                {renderedField}
              </Grid>
              <Grid item xs={6}>
                {nextRenderedField}
              </Grid>
            </Grid>
          )
          i += 2
        } else {
          renderedFields.push(
            <Grid item xs={6} key={field.name}>
              {renderedField}
            </Grid>
          )
          i++
        }
      } else {
        renderedFields.push(
          <Grid item xs={12} key={field.name}>
            {renderedField}
          </Grid>
        )
        i++
      }
    }

    return renderedFields
  }

  return (
    <Styles.Container>
      <Styles.FormContainer>
        <Grid container spacing={2}>
          {renderFormFields()}
        </Grid>
      </Styles.FormContainer>

      <Styles.PreviewContainer>
        <Styles.PreviewImage
          src={
            'https://res.cloudinary.com/openhouse-study/image/upload/v1753865882/assets/oma_assets/program-pafge/eca-page/announcementPreview.png'
          }
        />
      </Styles.PreviewContainer>
    </Styles.Container>
  )
}

export default AnnouncementForm
